<template>
  <div class="home-view">
    <!-- Welcome Section -->
    <h1 class="text-h4 font-weight-bold mb-8">Welcome Back {{ getUserDisplayName }}</h1>

    <!-- Statistics Cards -->
    <v-row class="stats-row mb-8">
      <!-- New Assessments -->
      <v-col cols="12" md="3">
        <v-card class="stat-card elevation-1" rounded>
          <v-card-text class="text-center">
            <h2 class="text-h2 font-weight-bold mb-1">{{ dashboardStats.new || 0 }}</h2>
            <p class="text-body-1 grey--text">New Assessments</p>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Completed Assessments -->
      <v-col cols="12" md="3">
        <v-card class="stat-card elevation-1" rounded>
          <v-card-text class="text-center">
            <h2 class="text-h2 font-weight-bold mb-1">{{ dashboardStats.completed || 0 }}</h2>
            <p class="text-body-1 grey--text">Completed Assessments</p>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- In Progress Assessments -->
      <v-col cols="12" md="3">
        <v-card class="stat-card elevation-1" rounded>
          <v-card-text class="text-center">
            <h2 class="text-h2 font-weight-bold mb-1">{{ dashboardStats.in_progress || 0 }}</h2>
            <p class="text-body-1 grey--text">In Progress</p>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Total Assigned -->
      <v-col cols="12" md="3">
        <v-card class="stat-card elevation-1" rounded>
          <v-card-text class="text-center">
            <h2 class="text-h2 font-weight-bold mb-1">{{ dashboardStats.total || 0 }}</h2>
            <p class="text-body-1 grey--text">Total Assigned</p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Assessment Activity Section -->
    <div class="survey-activity mb-8">
      <h2 class="text-h5 font-weight-bold mb-4">My Assigned Assessments</h2>

      <!-- Tabs and Filters -->
      <div class="d-flex align-center justify-space-between mb-4">
        <v-tabs 
          v-model="activeTab" 
          background-color="transparent" 
          class="survey-tabs"
          slider-color="#e94057"
          active-class="active-tab"
        >
          <v-tab value="all">All</v-tab>
          <v-tab value="new">New</v-tab>
          <v-tab value="in_progress">In Progress</v-tab>
          <v-tab value="completed">Completed</v-tab>
        </v-tabs>
        
        <div class="d-flex">
          <v-text-field
            v-model="searchQuery"
            prepend-inner-icon="mdi-magnify"
            hide-details
            filled
            dense
            placeholder="Search"
            class="search-field mr-2"
            @input="handleSearch"
          ></v-text-field>
        </div>
      </div>

      <!-- Assessment Table -->
      <v-card elevation-1 class="survey-table-card">
        <DataTable
          :headers="tableHeaders"
          :items="filteredSurveys"
          :loading="loading"
          :show-pagination="false"
          class="survey-table"
          :has-actions="true"
        >
          <!-- Status Column -->
          <template #column-status="{ item }">
            <v-chip
              small
              :color="getStatusColor(item.status)"
              text-color="white"
              label
            >
              {{ item.status }}
            </v-chip>
          </template>
          
          <!-- Progress Column -->
          <template #column-progress="{ item }">
            <div class="d-flex align-center">
              <div class="custom-progress-container mr-2">
                <div class="custom-progress-track">
                  <div 
                    class="custom-progress-fill" 
                    :style="{ width: Math.round(item.completion_percentage || 0) + '%' }"
                  ></div>
                </div>
              </div>
              <span class="text-caption font-weight-medium">{{ Math.round(item.completion_percentage || 0) }}%</span>
            </div>
          </template>
          
          <!-- Action Column -->
          <template #column-action="{ item }">
            <v-btn
              v-if="isDeadlinePassed(item)"
              color="#9e9e9e"
              variant="elevated"
              density="comfortable"
              prepend-icon="mdi-check-circle-outline"
              class="start-btn finished-btn"
            >
              Finished
            </v-btn>
            <v-btn
              v-else
              color="#e94057"
              variant="elevated"
              density="comfortable"
              prepend-icon="mdi-clipboard-check"
              @click="handleStartSurvey(item)"
              class="start-btn"
            >
              {{ getActionButtonText(item.status) }}
            </v-btn>
          </template>
        </DataTable>
      </v-card>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import { mapGetters } from 'vuex';
import DataTable from '@/components/DataTable.vue';

export default {
  name: 'HomeView',
  components: {
    DataTable
  },
  data() {
    return {
      loading: false,
      loadingStats: false,
      activeTab: 'all',
      searchQuery: '',
      expertProfile: null,
      dashboardStats: {
        total: 0,
        assigned: 0,
        new: 0,
        completed: 0,
        in_progress: 0,
      },
      tableHeaders: [
        { text: 'Assessment Title', value: 'title', align: 'start' },
        { text: 'Brand', value: 'brand' },
        { text: 'Status', value: 'status' },
        { text: 'Progress', value: 'progress' },
        { text: 'Creation date', value: 'creationDate' },
        { text: 'Deadline', value: 'deadline' },
        { text: 'Action', value: 'action', align: 'center', sortable: false },
      ],
      surveys: []
    };
  },
  computed: {
    ...mapGetters(['isAdmin', 'isSuperAdmin']),
    getUserDisplayName() {
      if (this.expertProfile && (this.expertProfile.first_name || this.expertProfile.last_name)) {
        return `${this.expertProfile.first_name || ''} ${this.expertProfile.last_name || ''}`.trim();
      }
      
      const user = this.$store.state.user;
      if (!user) return 'Guest';
      
      if (user.first_name && user.last_name) {
        return `${user.first_name} ${user.last_name}`;
      } else if (user.first_name) {
        return user.first_name;
      } else if (user.last_name) {
        return user.last_name;
      }
      return user.username || 'Guest';
    },
    filteredSurveys() {
      let filtered = [...this.surveys];
      
      // Filter by tab
      if (this.activeTab !== 'all') {
        filtered = filtered.filter(item => {
          switch(this.activeTab) {
            case 'new':
              return item.status === 'New';
            case 'completed':
              return item.status === 'Completed';
            case 'in_progress':
              return item.status === 'In Progress';
            default:
              return true;
          }
        });
      }
      
      // Filter by search query
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        filtered = filtered.filter(item => 
          item.brand.toLowerCase().includes(query) ||
          item.code.toLowerCase().includes(query) ||
          (item.title && item.title.toLowerCase().includes(query))
        );
      }
      
      return filtered;
    }
  },
  mounted() {
    // Redirect admins to assessments page since this is expert-only
    if (this.isAdmin) {
      this.$router.push('/assessments');
      return;
    }
    
    this.fetchExpertData();
    
    if (!this.$store.state.user) {
      this.$store.dispatch('fetchUserProfile');
    }
  },
  methods: {
    async fetchExpertData() {
      // Fetch assessments, stats, and expert profile in parallel
      await Promise.all([
        this.fetchExpertAssignedAssessments(),
        this.fetchExpertDashboardStats(),
        this.fetchExpertProfile()
      ]);
    },
    
    async fetchExpertProfile() {
      try {
        const response = await apiService.getExpertProfile();
        this.expertProfile = response.data;
      } catch (error) {
        console.error('Error fetching expert profile:', error);
        // Don't show error notification as this is not critical for the page functionality
        // and we have fallback logic in getUserDisplayName
      }
    },
    
    async fetchExpertAssignedAssessments() {
      this.loading = true;
      try {
        const response = await apiService.get('expert/assigned-assessments/');
        
        // The backend returns an array directly
        const assessments = response.data || [];
        
        this.surveys = assessments.map(assessment => ({
          id: assessment.id,
          title: assessment.title,
          brand: assessment.visual_identity_system_name || assessment.title || 'N/A',
          version: `v${assessment.version || 1}`,
          status: this.mapResponseStatusToDisplayStatus(assessment.response_status),
          code: assessment.id?.toString().padStart(8, '0') || '00000000',
          creationDate: this.formatDate(assessment.created_at),
          deadline: this.formatDate(assessment.deadline),
          deadlineRaw: assessment.deadline,
          completion_percentage: Math.max(0, Math.min(100, Number(assessment.completion_percentage) || 0)),
          response_status: assessment.response_status,
          is_active: assessment.is_active,
          is_draft: assessment.is_draft
        }));
        
        // Debug: Log the first few surveys to check completion_percentage values
        if (assessments.length > 0) {
          console.log('Sample assessment data:', assessments.slice(0, 3));
          console.log('Sample mapped surveys:', this.surveys.slice(0, 3));
        }
        
      } catch (error) {
        console.error('Error fetching expert assigned assessments:', error);
        this.surveys = [];
        
        // Show user-friendly error message
        this.$store.dispatch('showNotification', {
          message: 'Failed to load your assigned assessments. Please try again.',
          type: 'error'
        });
      } finally {
        this.loading = false;
      }
    },
    
    async fetchExpertDashboardStats() {
      this.loadingStats = true;
      try {
        const response = await apiService.get('expert/dashboard-stats/');
        
        if (response.data && typeof response.data === 'object') {
          this.dashboardStats = {
            total: response.data.total || 0,
            assigned: response.data.assigned || response.data.total || 0,
            new: response.data.new || 0,
            completed: response.data.completed || 0,
            in_progress: response.data.in_progress || 0,
          };
        } else {
          console.error('Unexpected expert dashboard stats format:', response.data);
          this.resetDashboardStats();
        }
        
      } catch (error) {
        console.error('Error fetching expert dashboard stats:', error);
        this.resetDashboardStats();
        
        // Show user-friendly error message
        this.$store.dispatch('showNotification', {
          message: 'Failed to load dashboard statistics. Please try again.',
          type: 'error'
        });
      } finally {
        this.loadingStats = false;
      }
    },
    
    resetDashboardStats() {
      this.dashboardStats = {
        total: 0,
        assigned: 0,
        new: 0,
        completed: 0,
        in_progress: 0,
      };
    },
    
    mapResponseStatusToDisplayStatus(responseStatus) {
      // Map backend response status to frontend display status
      switch (responseStatus) {
        case 'completed':
          return 'Completed';
        case 'in_progress':
        case 'started':
          return 'In Progress';
        case 'not_started':
        default:
          return 'New';
      }
    },
    
    getStatusColor(status) {
      switch (status.toLowerCase()) {
        case 'completed':
          return 'success';
        case 'new':
          return 'primary';
        case 'in progress':
          return 'warning';
        case 'pending':
          return 'orange';
        default:
          return 'grey';
      }
    },
    
    getActionButtonText(status) {
      switch (status) {
        case 'Completed':
          return 'Review';
        case 'In Progress':
          return 'Continue';
        case 'New':
        default:
          return 'Start';
      }
    },
    
    isDeadlinePassed(item) {
      if (!item.deadlineRaw) return false;
      const deadline = new Date(item.deadlineRaw);
      if (isNaN(deadline.getTime())) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      deadline.setHours(23, 59, 59, 999);
      return today > deadline;
    },
    
    handleStartSurvey(item) {
      if (this.isDeadlinePassed(item)) return;
      console.log('Starting/continuing assessment:', item);
      this.$router.push({ 
        name: 'completeAssessment', 
        params: { id: item.id } 
      });
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A';
        return `${date.toLocaleString('default', { month: 'short' })} ${date.getDate()} ${date.getFullYear()}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return 'N/A';
      }
    },
    
    handleSearch() {
      // Search is reactive through filteredSurveys
    }
  },
  watch: {
    // Watch for changes in tab to trigger filtering
    activeTab() {
      // Tab change is reactive through filteredSurveys
    }
  }
};
</script>

<style scoped>
.home-view {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
}

.stats-row {
  background-color: #f0f7fa;
  border-radius: 12px;
  padding: 20px;
  margin: 0;
}

.stat-card {
  background-color: white;
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-3px);
}

.survey-tabs {
  max-width: 600px;
}

.search-field {
  max-width: 500px;
  min-width: 400px;
}

.survey-table-card {
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.05);
}

.survey-table {
  width: 100%;
  padding: 0 16px;
}

/* Modify table style to add gaps between rows */
.survey-table :deep(table) {
  background-color: #f8f9fa; /* Page background color for gaps */
  border-collapse: separate;
  border-spacing: 0 8px; /* vertical gap between rows */
}

.survey-table :deep(tr) {
  background-color: white; /* Row background color */
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: background-color 0.2s, box-shadow 0.3s;
}

.survey-table :deep(tr:hover) {
  background-color: #f8f9fa; /* Hover row background color */
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.survey-table :deep(td) {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.survey-table :deep(th) {
  font-weight: 600 !important;
  color: rgba(0, 0, 0, 0.6) !important;
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  text-transform: none; /* Remove uppercase from default styles if any */
  font-size: inherit; /* Use default font size */
}

.survey-table :deep(.v-data-table__wrapper) {
  min-height: auto; /* Allow table to shrink if few items */
}

.start-btn {
  color: white !important;
  min-width: 100px;
}

.finished-btn {
  pointer-events: none;
  opacity: 0.85;
}

.active-tab {
  color: #e94057 !important;
}

.v-tab--selected {
  color: #e94057 !important;
}

/* Custom Progress Bar Styles */
.custom-progress-container {
  min-width: 80px;
  width: 80px;
}

.custom-progress-track {
  width: 100%;
  height: 8px;
  background-color: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.custom-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #e94057 0%, #f16575 100%);
  border-radius: 4px;
  transition: width 0.3s ease-in-out;
  min-width: 0;
}

/* Add a subtle animation for when progress updates */
.custom-progress-fill {
  animation: progressFill 0.3s ease-out;
}

@keyframes progressFill {
  from {
    transform: scaleX(0);
    transform-origin: left;
  }
  to {
    transform: scaleX(1);
    transform-origin: left;
  }
}
</style> 